import type { LatLng } from '~/_shared/types/latLng';
import {
  type AgsReportDataResponse, type ReportDataRequest,
} from '~/exportReport/exportReport.repository';
import type { ReportTableColumn } from '~/exportReport/table/ReportTable.model';
import type {
  ReportTemplate, ReportTemplateDataRow,
} from '~/exportReport/template/reportTemplate';

export type ReportDemographicData = {
  id: string;
  label: string;
  value: number | string |null;
};

export const normalizeReportDemographicId = (demographicId: string): string => demographicId.replace('%', '');

export const getReportTemplateDataRowDemographicId = (row: ReportTemplateDataRow): string => {
  return row.id.startsWith('%') && row.pct_id ? row.pct_id : row.id;
};

export const getFormulaRowsById = (template: ReportTemplate): ReadonlyMap<string, ReportTemplateDataRow> => {
  return new Map(template.items.flatMap(item => item.type === 'table'
    ? item.content.flatMap(row => row.type === 'data-row' && row.formula ? [[row.id, row] as const] : [])
    : []));
};

export const getReportTemplateDemographicIds = (template: ReportTemplate): ReadonlyArray<string> => {
  return [...template.items.reduce<Set<string>>((demographicIds, item) => {
    if (item.type !== 'table') {
      return demographicIds;
    }

    item.content.forEach(row => {
      if (row.type !== 'data-row') {
        return;
      }

      demographicIds.add(getReportTemplateDataRowDemographicId(row));
    });

    return demographicIds;
  }, new Set())];
};

const getReportAreaData = (data: AgsReportDataResponse | null, columnId: string): AgsReportDataResponse['areas'][number] | undefined => {
  if (!data?.areas.length) {
    return undefined;
  }

  if (data.areas.length === 1) {
    return data.areas[0];
  }

  return data.areas.find(area => area.type === 'ring' && area.extent?.toString() === columnId);
};

export const getReportDemographicDataFromResults = (
  data: AgsReportDataResponse | null,
  columnId: string,
  demographicId: string
): ReportDemographicData | null => {
  const columnData = getReportAreaData(data, columnId);
  if (!columnData) {
    return null;
  }

  const resultData = columnData.data.find(demographic => demographic.tag === demographicId);
  const numericalValue = Number(resultData?.value);

  return resultData ? {
    value: isNaN(numericalValue) ? resultData?.value.toString() : numericalValue,
    label: resultData.description,
    id: demographicId,
  } : null;
};

export const getReportDemographicLabelFromResults = (
  data: AgsReportDataResponse | null,
  demographicId: string
): string | null => {
  if (!data?.areas[0]?.data) {
    return null;
  }

  const demographic = data.areas[0].data.find(demographicData => normalizeReportDemographicId(demographicData.tag) === normalizeReportDemographicId(demographicId));
  return demographic?.description ?? null;
};

export const createAgsReportDataRingRequest = ({
  center, returnType, tableColumns, varlistId,
}: {
  center: LatLng;
  returnType?: ReportDataRequest['returnType'];
  tableColumns: ReadonlyArray<ReportTableColumn>;
  varlistId: string;
}): ReportDataRequest => {
  const radiiInMiles = tableColumns
    .filter(column => column.type === 'radius')
    .map(column => parseInt(column.id, 10))
    .filter(radius => !isNaN(radius));

  return {
    lat: center.lat,
    lng: center.lng,
    radiiInMiles,
    country: 'usa',
    returnType,
    varlistId,
  };
};
